import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { MuiTelInput } from "mui-tel-input";
import TrialCrc from "../PopUp/TrialCrc";
import AddCrcModal from "../PopUp/AddCrcModal";
import useGetCoordinators from "../../hooks/useGetCoordinators";
import { USER_ROLES, INV_RELATED_ROLES } from "../../utils/constants";

const InvestigatorStaffList = ({
  investigatorEmail,
  trialId,
  roleType,
  roleLabel,
  canManageCrc,
  onAddClick,
}) => {
  const { data } = useGetCoordinators({
    coordinator: roleType,
    headUser: investigatorEmail,
    trialId,
  });
  const staff = data?.results || [];

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 2 }}>
      {staff.map((member) => (
        <Chip key={member.id} size="small" label={member.email} />
      ))}
      {canManageCrc && (
        <Button size="small" onClick={onAddClick}>
          + Add {roleLabel}
        </Button>
      )}
    </Box>
  );
};

const ManualInvestigatorSIteId = ({
  trialDetails,
  setTrialDetails,
  setInfoSettingChanged,
  investigatorChanged,
  setInvestigatorChanged,
  setChangeTrialSetting,
  allInvestigators,
  onInvestigatorChange,
  handleBasicInfoUpdate,
  AllInvestigatorsData,
  setAllInvestigators,
}) => {
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data.role;

  const [isDisable, setIsDisable] = useState(true);
  const [showModal, setShowModal] = useState("");
  const [errorMsg, seterrorMsg] = useState("");
  const [drugErrorMsg, setDrugerrorMsg] = useState("");
  const [focusedInputIndex, setFocusedInputIndex] = useState(null);
  const [drugfocusedInput, setDrugfocusedInput] = useState(null);
  const [maxPatientFocusedInput, setMaxPatientFocusedInput] = useState(null);
  const [staffModalState, setStaffModalState] = useState(null);
  const canManageCrc = !INV_RELATED_ROLES.includes(role) && role !== USER_ROLES.REVIEWER;

  const investigators = trialDetails?.investigators;
  const settingToUpdate = trialDetails?.site_id_setting;

  const handleSiteIdChange = (e, index) => {
    const newSiteNumber = e.target.value;
    setFocusedInputIndex(index);

    setTrialDetails((prevState) => {
      const updatedInvestigators = [...prevState?.investigators];

      const isSiteNumberExists = updatedInvestigators.some(
        (investigator, i) => i !== index && investigator.site_number === newSiteNumber
      );

      if (isSiteNumberExists) {
        seterrorMsg("This Site already exists!");
        setInvestigatorChanged(false);
      } else {
        seterrorMsg("");
        setInvestigatorChanged(true);
      }

      updatedInvestigators[index] = {
        ...updatedInvestigators[index],
        site_number: newSiteNumber,
      };
      return { ...prevState, investigators: updatedInvestigators };
    });
  };

  const handleMinDrugChange = (e, index) => {
    const minDrugValue = e.target.value;
    setDrugfocusedInput(index);

    setTrialDetails((prevState) => {
      const updatedInvestigators = [...prevState?.investigators];
      setInvestigatorChanged(true);
      setDrugerrorMsg("");
      updatedInvestigators[index] = {
        ...updatedInvestigators[index],
        minimum_drug: minDrugValue,
      };
      return { ...prevState, investigators: updatedInvestigators };
    });
  };

  const handleSettingTypeChange = (event) => {
    const { name, value } = event.target;
    setTrialDetails((prevDetails) => ({
      ...prevDetails,
      site_id_setting: {
        ...prevDetails.site_id_setting,
        [name]: value,
        id_change: true,
      },
    }));
    setInfoSettingChanged(true);
    setInvestigatorChanged(true);
  };

  const handleAddSuccess = () => {
    AllInvestigatorsData();
  };

  const handleBooleanFields = (isManual) => {
    setTrialDetails({
      ...trialDetails,
      manual_investigator_id: isManual,
      site_id_setting: {
        ...trialDetails.site_id_setting,
        id_change: true,
      },
    });
    setInvestigatorChanged(true);
  };

  useEffect(() => {
    setChangeTrialSetting(trialDetails?.site_id_setting);
  }, [trialDetails?.site_id_setting]);

  useEffect(() => {
    if (trialDetails?.manual_investigator_id === true) {
      setIsDisable(false);
      setTrialDetails((prevTrialDetails) => ({
        ...prevTrialDetails,
        site_id_setting: {
          ...prevTrialDetails.site_id_setting,
          setting_type: "Manual",
        },
      }));
    } else {
      setIsDisable(true);
      setTrialDetails((prevTrialDetails) => ({
        ...prevTrialDetails,
        site_id_setting: {
          ...prevTrialDetails.site_id_setting,
          setting_type: "Sequence",
        },
      }));
    }
  }, [trialDetails?.manual_investigator_id]);

  const handleSave = () => {
    const isEmpty = trialDetails?.investigators.some((inv) => !inv?.site_number && !isDisable);
    const drugEmptyField = trialDetails?.investigators.some((inv) => !inv?.minimum_drug);
    const maxPatientField = trialDetails?.investigators.some((inv) => !inv?.max_patient);
    if (isEmpty) {
      const firstEmptyIndex = trialDetails?.investigators.findIndex(
        (inv) => !inv?.site_number && !isDisable
      );
      setFocusedInputIndex(firstEmptyIndex);
      seterrorMsg("Please fill this field");
    } else if (maxPatientField) {
      const firstEmptyIndex = trialDetails?.investigators.findIndex((inv) => !inv?.max_patient);
      setMaxPatientFocusedInput(firstEmptyIndex);
      toast.error("Please fill the Max Subjects field");
    } else {
      handleBasicInfoUpdate();
    }
  };

  const handleMaxPatientChange = (e, index) => {
    const newMaxPatientValue = e.target.value;
    setMaxPatientFocusedInput(index);

    setTrialDetails((prevState) => {
      const updatedInvestigators = [...prevState?.investigators];

      updatedInvestigators[index] = {
        ...updatedInvestigators[index],
        max_patient: newMaxPatientValue,
      };

      return { ...prevState, investigators: updatedInvestigators };
    });

    setInvestigatorChanged(true);
  };

  const handleSiteAddressChange = (e, index) => {
    const newSiteAddress = e.target.value;

    setTrialDetails((prevState) => {
      const updatedInvestigators = [...prevState?.investigators];

      updatedInvestigators[index] = {
        ...updatedInvestigators[index],
        site_address: newSiteAddress,
      };

      return { ...prevState, investigators: updatedInvestigators };
    });

    setInvestigatorChanged(true);
  };

  const handleSitePhoneChange = (value, index) => {
    setTrialDetails((prevState) => {
      const updatedInvestigators = [...prevState?.investigators];

      updatedInvestigators[index] = {
        ...updatedInvestigators[index],
        site_phone: value,
      };

      return { ...prevState, investigators: updatedInvestigators };
    });

    setInvestigatorChanged(true);
  };

  return (
    <Box>
      {trialDetails?.is_setting && trialDetails.trial_structure !== "session_based" && (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <RadioGroup
              row
              value={trialDetails?.manual_investigator_id ? "manual" : "automatic"}
              onChange={(e) => handleBooleanFields(e.target.value === "manual")}
            >
              <FormControlLabel value="automatic" control={<Radio />} label="Automatic" />
              <FormControlLabel value="manual" control={<Radio />} label="Manual" />
            </RadioGroup>

            {role !== "Investigator" && role !== "Inv_coordinator" && role !== "Reviewer" && (
              <Button
                variant="contained"
                size="small"
                id="site-settings"
                onClick={() => handleSave()}
                disabled={!investigatorChanged}
              >
                Save
              </Button>
            )}
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
              mt: 2,
            }}
          >
            <TextField
              fullWidth
              label="Lower Value"
              size="small"
              type="number"
              inputProps={{ min: 1 }}
              name="lower_range"
              value={settingToUpdate?.lower_range || ""}
              onChange={handleSettingTypeChange}
              placeholder="Lower Value"
            />
            <TextField
              fullWidth
              label="Upper Value"
              size="small"
              type="number"
              inputProps={{ min: 1 }}
              name="upper_range"
              value={settingToUpdate?.upper_range || ""}
              onChange={handleSettingTypeChange}
              placeholder="Upper Value"
              required
            />
          </Box>
        </>
      )}

      {role !== "Investigator" &&
        role !== "Inv_coordinator" &&
        role !== "Reviewer" &&
        trialDetails.investigators &&
        trialDetails.investigators.length > 0 && (
          <Box sx={{ mb: 3, mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Investigators
            </Typography>
            <Autocomplete
              multiple
              id="investigators-select-setting"
              value={trialDetails.investigators || []}
              options={allInvestigators}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={onInvestigatorChange}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={option.label}
                      size="small"
                      {...tagProps}
                      {...(option.isFixed && { onDelete: undefined })}
                    />
                  );
                })
              }
              renderInput={(params) => <TextField {...params} size="small" />}
            />
          </Box>
        )}

      {trialDetails?.is_setting &&
        trialDetails.trial_structure === "session_based" &&
        role !== "Investigator" &&
        role !== "Inv_coordinator" &&
        role !== "Reviewer" && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleSave()}
              disabled={!investigatorChanged}
            >
              Save
            </Button>
          </Box>
        )}

      {trialDetails?.is_setting && (
        <>
          {investigators?.map((inv, index) => (
            <Box key={index}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    trialDetails.trial_structure === "session_based"
                      ? { xs: "1fr", md: "1fr 1fr" }
                      : { xs: "1fr", md: "1fr 1fr 1fr" },
                  gap: 2,
                  mb: 2,
                }}
              >
                <TextField
                  fullWidth
                  label="Email"
                  size="small"
                  type="email"
                  value={inv?.label || ""}
                  disabled
                />
                {trialDetails.trial_structure !== "session_based" && (
                  <Box>
                    <TextField
                      fullWidth
                      label="Site Id"
                      size="small"
                      type="number"
                      inputProps={{ min: 1 }}
                      value={inv?.site_number || ""}
                      onChange={(e) => handleSiteIdChange(e, index)}
                      disabled={isDisable || !!inv?.isFixed}
                      required
                    />
                    {focusedInputIndex === index && errorMsg && (
                      <Typography variant="caption" color="error">
                        {errorMsg}
                      </Typography>
                    )}
                  </Box>
                )}
                <TextField
                  fullWidth
                  label="Max Subjects"
                  size="small"
                  type="number"
                  name="max_patient"
                  inputProps={{ min: 1 }}
                  value={inv?.max_patient || ""}
                  onChange={(e) => handleMaxPatientChange(e, index)}
                  required
                />
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                  mb: 2,
                }}
              >
                <TextField
                  fullWidth
                  label="Site Address"
                  size="small"
                  value={inv?.site_address || ""}
                  onChange={(e) => handleSiteAddressChange(e, index)}
                />
                <MuiTelInput
                  fullWidth
                  label="Site Phone No."
                  size="small"
                  defaultCountry="US"
                  disableFormatting
                  value={inv?.site_phone || ""}
                  onChange={(value) => handleSitePhoneChange(value, index)}
                />
              </Box>
              <InvestigatorStaffList
                investigatorEmail={inv?.label}
                trialId={trialDetails?.id}
                roleType={USER_ROLES.CRC}
                roleLabel="CRC"
                canManageCrc={canManageCrc}
                onAddClick={() =>
                  setStaffModalState({
                    investigator: inv,
                    roleType: USER_ROLES.CRC,
                    roleLabel: "CRC",
                  })
                }
              />
              <InvestigatorStaffList
                investigatorEmail={inv?.label}
                trialId={trialDetails?.id}
                roleType={USER_ROLES.SUB_INVESTIGATOR}
                roleLabel="Sub Investigator"
                canManageCrc={canManageCrc}
                onAddClick={() =>
                  setStaffModalState({
                    investigator: inv,
                    roleType: USER_ROLES.SUB_INVESTIGATOR,
                    roleLabel: "Sub Investigator",
                  })
                }
              />
            </Box>
          ))}
        </>
      )}

      {staffModalState && (
        <AddCrcModal
          investigator={staffModalState.investigator}
          trialId={trialDetails?.id}
          roleType={staffModalState.roleType}
          roleLabel={staffModalState.roleLabel}
          onClose={() => setStaffModalState(null)}
        />
      )}

      {showModal === true && (
        <TrialCrc
          setShowModal={setShowModal}
          toast={toast}
          type="investigator"
          onAddSuccess={handleAddSuccess}
          setTrialDetails={setTrialDetails}
          setAllInvestigators={setAllInvestigators}
          allInvestigators={allInvestigators}
          onInvestigatorChange={onInvestigatorChange}
          trialDetails={trialDetails}
        />
      )}

      <Button
        variant="contained"
        size="small"
        sx={{ mt: 1, mb: 3 }}
        onClick={() => setShowModal(true)}
      >
        Invite New Investigator
      </Button>
    </Box>
  );
};

export default ManualInvestigatorSIteId;
